import { useEffect, useState } from 'react';

import { forumApi } from '@/services/endpoints/forum';

import { forumSearchTerm } from './forum-display';
import type { PostListItem, SortOption } from './types';

/** How long the search box has to sit still before its term goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;

interface ServerPostSearchParams {
  /** The search box's text, as typed. */
  query: string;
  /**
   * Whether the API can search: the loaded rows carry `excerpt`
   * (`searchesOnServer`). An API without ai2aim-ems-api#2889 ignores `search`
   * and answers the unfiltered page, so nothing is sent until this is true.
   */
  enabled: boolean;
  /** The space to search. Omit for every space in the organization. */
  subforumId?: string;
  sort?: SortOption;
  limit?: number;
}

interface SearchAnswer {
  key: string;
  posts: PostListItem[];
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * The search box's term matched on the server (T100526003), over every post
 * the list can show rather than only the rows already loaded, which is what
 * keeps a search over post bodies working once list rows stop carrying them.
 *
 * Mirrors the portal (employee-management-system#2313): the trimmed, capped
 * term is sent 300 ms after the last keystroke, and only once `enabled`.
 *
 * Returns the matching rows, or `undefined` when there is no server answer for
 * the current term: no term, an API that cannot search, the first answer still
 * in flight, or a failed request. The caller then filters its loaded rows with
 * `matchesSearch`, as it did before the API could search.
 */
export function useServerPostSearch({
  query,
  enabled,
  subforumId,
  sort,
  limit,
}: ServerPostSearchParams): PostListItem[] | undefined {
  const term = forumSearchTerm(query);
  const debouncedTerm = useDebouncedValue(term, SEARCH_DEBOUNCE_MS);
  const active = enabled && term !== '' && debouncedTerm !== '';
  // One string per distinct request, so an answer is used only for the request
  // that asked for it, and a slower answer to an older term is dropped.
  const key = active
    ? JSON.stringify([subforumId ?? null, sort ?? null, limit ?? null, debouncedTerm])
    : '';
  const [answer, setAnswer] = useState<SearchAnswer | null>(null);

  useEffect(() => {
    if (!key) return;
    let current = true;
    forumApi
      .getPosts({ subforumId, sort, limit, search: debouncedTerm })
      .then((response) => {
        if (current) setAnswer({ key, posts: response.data ?? [] });
      })
      .catch((error) => {
        // No answer is kept, so the caller keeps filtering its loaded rows.
        console.error('Forum search failed:', error);
      });
    return () => {
      current = false;
    };
  }, [key, subforumId, sort, limit, debouncedTerm]);

  return active && answer?.key === key ? answer.posts : undefined;
}
